import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getSiteConfig, type SiteConfig } from '@/services/siteConfigService';

interface SiteConfigContextType {
  config: SiteConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);

interface SiteConfigProviderProps {
  children: ReactNode;
  initialConfig?: SiteConfig; // SSR 注入的初始配置
}

// 全局缓存配置，避免每次路由切换都重新请求
let cachedConfig: SiteConfig | null = null;
let configPromise: Promise<SiteConfig | null> | null = null;

export const SiteConfigProvider = ({ children, initialConfig }: SiteConfigProviderProps) => {
  // 优先使用缓存的配置
  const [config, setConfig] = useState<SiteConfig | null>(initialConfig || cachedConfig || null);
  const [loading, setLoading] = useState(!initialConfig && !cachedConfig); // 如果有初始数据或缓存，不需要 loading
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    // 如果已经有请求在进行中，直接返回该 Promise
    if (configPromise) {
      const result = await configPromise;
      if (result) {
        setConfig(result);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 创建并缓存 Promise，避免并发请求
      configPromise = getSiteConfig().then((config) => {
        if (config) {
          cachedConfig = config; // 缓存到全局
          return config;
        } else {
          throw new Error('获取站点配置失败');
        }
      });

      const data = await configPromise;

      if (data) {
        setConfig(data);
      }
    } catch (err: any) {
      setError(err.message || '网络错误，无法获取站点配置');
      console.error('Failed to fetch site config:', err);
    } finally {
      setLoading(false);
      configPromise = null; // 清空 Promise 缓存
    }
  };

  useEffect(() => {
    // 如果已经有初始配置或缓存，不再重新请求
    // 注意：title 和 favicon 都由服务器端处理
    if (initialConfig || cachedConfig) {
      // 缓存 initialConfig 到全局，供后续路由使用
      if (initialConfig && !cachedConfig) {
        cachedConfig = initialConfig;
      }
    } else {
      // 没有初始配置且没有缓存时才请求
      fetchConfig();
    }
  }, []);

  return (
    <SiteConfigContext.Provider value={{ config, loading, error, refetch: fetchConfig }}>
      {children}
    </SiteConfigContext.Provider>
  );
};

/**
 * 使用站点配置的 Hook
 */
export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
};
